/**
 * V5.7: 동기화 로그 패널
 *
 * 기능:
 * - 동기화 로그 목록 조회
 * - 펼침/접힘 토글
 * - 상태별 아이콘/색상
 * - 연도별 상세 결과
 *
 * V5.7 변경:
 * - Tailwind 클래스 제거, CSS 모듈로 전환
 */

import React, { useState } from 'react';
import {
  Clock,
  RefreshCw,
  ChevronUp,
  ChevronDown,
  CheckCircle2,
  AlertCircle,
  XCircle,
} from 'lucide-react';
import {
  HolidaySyncLog,
  SyncStatus,
  CompanyCode,
  SYNC_STATUS_LABELS,
  TRIGGERED_BY_LABELS,
  formatTimestamp,
} from './types';
import styles from './holidaysStyles.module.css';

/**
 * V5.12.2: SyncLogsPanel Props
 * - error prop 제거, isForbidden prop 추가
 * - companyCode: 헤더 표시용 (필수)
 */
interface SyncLogsPanelProps {
  logs: HolidaySyncLog[];
  isLoading: boolean;
  companyCode: CompanyCode;
  onRefresh: () => void;
  isForbidden?: boolean; // V5.12.2: 권한 없음 상태
}

export function SyncLogsPanel({
  logs,
  isLoading,
  companyCode,
  onRefresh,
  isForbidden = false,
}: SyncLogsPanelProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  // V5.12.2: isForbidden이면 빈 로그로 표시
  const displayLogs = isForbidden ? [] : logs;
  const displayCount = isForbidden ? 0 : logs.length;

  return (
    <div className={styles.syncLogsContainer}>
      {/* 토글 헤더 */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className={styles.syncLogsToggle}
      >
        <Clock />
        최근 동기화 로그
        <span className={styles.syncLogsCount}>({displayCount}건)</span>
        {isExpanded ? <ChevronUp /> : <ChevronDown />}
      </button>

      {/* 로그 목록 */}
      {isExpanded && (
        <div className={styles.syncLogsPanel}>
          {/* 헤더 */}
          <div className={styles.syncLogsPanelHeader}>
            <span className={styles.syncLogsPanelTitle}>
              {companyCode === 'KR' ? '한국' : '중국'} 동기화 이력
            </span>
            <button
              onClick={onRefresh}
              disabled={isLoading}
              className={styles.syncLogsPanelRefresh}
              title="새로고침"
            >
              <RefreshCw className={isLoading ? styles.spinning : ''} />
            </button>
          </div>

          {/* 내용 */}
          {isLoading ? (
            <div className={styles.syncLogsEmpty}>
              <div className={styles.syncLogsLoading}>
                <div className={styles.syncLogsSpinner} />
                로딩 중...
              </div>
            </div>
          ) : displayLogs.length === 0 ? (
            <div className={styles.syncLogsEmpty}>
              동기화 로그가 없습니다.
            </div>
          ) : (
            <div className={styles.syncLogsList}>
              {displayLogs.map((log) => (
                <SyncLogItem key={log.id} log={log} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// 로그 아이템 컴포넌트
// ============================================================================

interface SyncLogItemProps {
  log: HolidaySyncLog;
}

function SyncLogItem({ log }: SyncLogItemProps) {
  const [expanded, setExpanded] = useState(false);

  // 상태 아이콘
  const StatusIcon = () => {
    switch (log.status) {
      case 'success':
        return <CheckCircle2 className={styles.syncLogStatusSuccess} />;
      case 'partial':
        return <AlertCircle className={styles.syncLogStatusPartial} />;
      case 'fail':
        return <XCircle className={styles.syncLogStatusFail} />;
    }
  };

  // 상태 배지 클래스
  const getStatusBadgeClass = (status: SyncStatus): string => {
    switch (status) {
      case 'success':
        return styles.syncLogBadgeSuccess;
      case 'partial':
        return styles.syncLogBadgePartial;
      case 'fail':
        return styles.syncLogBadgeFail;
    }
  };

  return (
    <div className={styles.syncLogItem}>
      {/* 메인 행 */}
      <div
        className={styles.syncLogItemHeader}
        onClick={() => setExpanded(!expanded)}
      >
        <div className={styles.syncLogItemLeft}>
          <StatusIcon />
          <div className={styles.syncLogItemInfo}>
            <div className={styles.syncLogItemBadges}>
              <span className={styles.syncLogItemYear}>
                {log.years.join(', ')}년
              </span>
              <span className={getStatusBadgeClass(log.status)}>
                {SYNC_STATUS_LABELS[log.status]}
              </span>
              <span className={styles.syncLogItemTrigger}>
                {TRIGGERED_BY_LABELS[log.triggeredBy]}
              </span>
            </div>
            <div className={styles.syncLogItemDate}>
              {formatTimestamp(log.finishedAt)}
            </div>
          </div>
        </div>
        <div className={styles.syncLogItemRight}>
          <div className={styles.syncLogItemStats}>
            <span className={styles.syncLogStatCreated}>+{log.totalCreatedCount}</span>
            {' / '}
            <span className={styles.syncLogStatUpdated}>{log.totalUpdatedCount} 수정</span>
            {' / '}
            <span className={styles.syncLogStatSkipped}>{log.totalSkippedCount} 스킵</span>
          </div>
          {expanded ? (
            <ChevronUp className={styles.syncLogChevron} />
          ) : (
            <ChevronDown className={styles.syncLogChevron} />
          )}
        </div>
      </div>

      {/* 상세 정보 (펼침) */}
      {expanded && (
        <div className={styles.syncLogItemDetail}>
          {/* 모바일용 요약 */}
          <div className={styles.syncLogItemStatsMobile}>
            생성: {log.totalCreatedCount} / 수정: {log.totalUpdatedCount} / 스킵:{' '}
            {log.totalSkippedCount}
          </div>

          {/* 연도별 결과 */}
          {log.results.map((result) => (
            <div
              key={result.year}
              className={result.success ? styles.syncLogResultSuccess : styles.syncLogResultFail}
            >
              <div className={styles.syncLogResultHeader}>
                <span className={styles.syncLogResultYear}>{result.year}년</span>
                <span>
                  {result.success ? (
                    <span className={styles.syncLogResultStatusSuccess}>성공</span>
                  ) : (
                    <span className={styles.syncLogResultStatusFail}>실패</span>
                  )}
                </span>
              </div>
              <div className={styles.syncLogResultStats}>
                생성: {result.createdCount} / 수정: {result.updatedCount} / 스킵:{' '}
                {result.skippedCount}
              </div>
              {result.error && (
                <div className={styles.syncLogResultError}>{result.error}</div>
              )}
            </div>
          ))}

          {/* 에러 목록 */}
          {log.errors.length > 0 && (
            <div className={styles.syncLogErrors}>
              <strong>오류:</strong>
              <ul className={styles.syncLogErrorList}>
                {log.errors.map((err, idx) => (
                  <li key={idx}>{err}</li>
                ))}
              </ul>
            </div>
          )}

          {/* 메타 정보 */}
          <div className={styles.syncLogMeta}>
            시작: {formatTimestamp(log.startedAt)}
          </div>
        </div>
      )}
    </div>
  );
}
